import React, { useState, useRef, useEffect } from 'react';
import liff from '@line/liff';
import { Html5Qrcode, Html5QrcodeSupportedFormats, Html5QrcodeScannerState } from 'html5-qrcode';
import { parseInvoiceQr } from '../../utilities/InvoiceQrParser';
import styles from './InvoiceScanner.module.scss';

// 掃描發票 — a QR-frame icon at the right edge of the 款項 field.
// Inside the LINE app it uses the native scanner (liff.scanCodeV2); everywhere
// else (desktop / mobile browser) it opens an in-page camera scanner via
// html5-qrcode, which triggers the browser's camera-permission prompt.
//
// It reads the LEFT QR of a Taiwan 電子發票 and reports the parsed
// { amount, isoDate, ... } so the form can autofill 金額 and 日期.
// Description (款項) stays manual.

const READER_ID = 'invoice-qr-reader';

// Map getUserMedia / scanner errors to a friendly Chinese hint.
const cameraErrorHint = (err) => {
  const name = err && err.name;
  if (name === 'NotAllowedError' || name === 'SecurityError') {
    return '相機權限被拒絕，請到瀏覽器設定開啟相機權限';
  }
  if (name === 'NotFoundError' || name === 'OverconstrainedError') {
    return '找不到可用的相機';
  }
  if (name === 'NotReadableError') {
    return '相機正被其他程式使用，請關閉後再試';
  }
  return '無法開啟相機，請確認權限與裝置後再試';
};

const InvoiceScanner = ({ onScanned }) => {
  const [scanning, setScanning] = useState(false);
  const [webOpen, setWebOpen] = useState(false);
  const [hint, setHint] = useState('將發票左側的 QR code 對準框內，靠近一點更易讀取');
  const scannerRef = useRef(null);
  const handledRef = useRef(false);

  const inLineApp = () => {
    try {
      return typeof liff?.isInClient === 'function' && liff.isInClient();
    } catch (e) {
      return false;
    }
  };

  const closeWeb = () => setWebOpen(false);

  const handleScan = async () => {
    // Native LINE scanner when running inside the LINE app.
    if (inLineApp() && typeof liff.scanCodeV2 === 'function') {
      if (scanning) { return; }
      setScanning(true);
      try {
        const result = await liff.scanCodeV2();
        const parsed = parseInvoiceQr(result && result.value);
        if (parsed) {
          onScanned(parsed);
        } else {
          // eslint-disable-next-line no-alert
          window.alert('無法辨識發票，請掃描電子發票「左側」的 QR code');
        }
      } catch (e) {
        // User cancelled or platform unsupported — stay silent.
      } finally {
        setScanning(false);
      }
      return;
    }

    // Web fallback: open the in-page camera scanner.
    handledRef.current = false;
    setHint('將發票左側的 QR code 對準框內，靠近一點更易讀取');
    setWebOpen(true);
  };

  // Start / stop the web camera scanner while the overlay is open.
  useEffect(() => {
    if (!webOpen) { return undefined; }

    let cancelled = false;
    let scanner = null;

    // Only stop a scanner that is actually running — html5-qrcode throws
    // synchronously otherwise (e.g. when cleanup races an in-flight start).
    const stopSafely = (s) => {
      if (!s) { return; }
      try {
        const state = s.getState ? s.getState() : null;
        if (state === Html5QrcodeScannerState.SCANNING
          || state === Html5QrcodeScannerState.PAUSED) {
          s.stop().then(() => { try { s.clear(); } catch (e) { /* noop */ } }).catch(() => {});
        } else {
          try { s.clear(); } catch (e) { /* noop */ }
        }
      } catch (e) {
        /* not running — nothing to stop */
      }
    };

    const run = async () => {
      // Let html5-qrcode acquire the camera directly — its own start() triggers
      // the browser permission prompt. (A separate getUserMedia probe races the
      // camera and breaks the subsequent start.)
      scanner = new Html5Qrcode(READER_ID, {
        formatsToSupport: [Html5QrcodeSupportedFormats.QR_CODE],
        verbose: false,
      });
      scannerRef.current = scanner;

      try {
        await scanner.start(
          // Camera selector MUST be a single-key object for html5-qrcode.
          { facingMode: 'environment' },
          {
            fps: 10,
            // The decode canvas is sized to the qrbox's on-screen CSS pixels,
            // so a bigger box = more pixels = the dense 發票 QR actually resolves.
            // Use ~95% of the shorter side. Must stay <= the viewfinder or
            // html5-qrcode throws (getShadedRegionBounds), so no hard floor.
            qrbox: (viewW, viewH) => {
              const edge = Math.floor(Math.min(viewW, viewH) * 0.95);
              return { width: edge, height: edge };
            },
            // Ask for a taller 4:3 frame (not 16:9) so the displayed video is
            // taller -> the square decode box can be bigger -> denser QR resolves.
            // Full MediaTrackConstraints are allowed here.
            videoConstraints: {
              facingMode: 'environment',
              width: { ideal: 1440 },
              height: { ideal: 1080 },
              aspectRatio: { ideal: 1.3333 },
            },
            // NOTE: deliberately NOT enabling useBarCodeDetectorIfSupported —
            // the bundled ZXing decoder reads these dense 發票 QRs reliably,
            // whereas the native BarcodeDetector often fails to detect them.
          },
          (decodedText) => {
            if (handledRef.current) { return; }
            const parsed = parseInvoiceQr(decodedText);
            if (parsed) {
              handledRef.current = true;
              onScanned(parsed);
              closeWeb();
            } else {
              setHint('這不是發票 QR code，請掃描發票「左側」那個');
            }
          },
          () => {}, // per-frame decode miss — ignore (would spam the console)
        );
        if (cancelled) { stopSafely(scanner); }
      } catch (err) {
        if (!cancelled) {
          // eslint-disable-next-line no-console
          console.error('[InvoiceScanner] camera start failed:', err && err.name, err);
          setHint(cameraErrorHint(err));
        }
      }
    };

    // Defer slightly so React StrictMode's throwaway mount cancels before we
    // actually touch the camera (avoids a double-acquire race).
    const timer = setTimeout(run, 60);

    return () => {
      cancelled = true;
      clearTimeout(timer);
      stopSafely(scanner || scannerRef.current);
      scannerRef.current = null;
    };
  }, [webOpen]);

  return (
    <>
      <button
        type="button"
        className={styles.trigger}
        onClick={handleScan}
        aria-label="掃描發票"
        disabled={scanning}
      >
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor"
          strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M3 8V5a2 2 0 0 1 2-2h3" />
          <path d="M21 8V5a2 2 0 0 0-2-2h-3" />
          <path d="M3 16v3a2 2 0 0 0 2 2h3" />
          <path d="M21 16v3a2 2 0 0 1-2 2h-3" />
          <path d="M7 12h10" />
        </svg>
      </button>

      {webOpen ? (
        <div className={styles.overlay} role="dialog" aria-label="掃描發票">
          <div className={styles.sheet}>
            <div className={styles.header}>
              <span className={styles.title}>掃描發票</span>
              <button
                type="button"
                className={styles.close}
                onClick={closeWeb}
                aria-label="關閉"
              >
                ✕
              </button>
            </div>
            <div id={READER_ID} className={styles.reader} />
            <div className={styles.hint}>{hint}</div>
          </div>
        </div>
      ) : null}
    </>
  );
};

export default InvoiceScanner;
